import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Smartphone, LoaderCircle } from "lucide-react";
import Modal from "../portal/Modal";
import GrayButton from "../UI/buttonUI/GrayButton";
import useApiStatus from "../../CustomHooks/useApiStatus";
import { verifyPhoneSendOtp, verifyPhoneOtp } from "../../lib/sessionClient";
import OtpInput from "./OtpInput";
import useOtpInput from "../../CustomHooks/useOtpInput";
import { handleErrMessage } from "../../functions/handleErrMessage";
import success from "../../assets/icons/successChangePass.svg";

const VIEW = {
  SEND: "SEND",
  VERIFY: "VERIFY",
  SUCCESS: "SUCCESS",
};

const MobileVerify = ({ phone, setOpen }) => {
  const navigate = useNavigate();
  const [sendStatus, sendRunner] = useApiStatus();
  const [verifyStatus, verifyRunner] = useApiStatus();
  const [view, setView] = useState(VIEW.SEND);

  const { loading: sendLoading, error: sendError } = sendStatus;

  const { loading: verifyLoading, error: verifyError } = verifyStatus;

  const { otp, inputRefs, handleOtpChange, handleKeyDown, handlePaste } =
    useOtpInput();

  // ===============================================
  // VERIFY PHONE SEND OTP HANDLER
  // ===============================================
  const sendOtpHandler = () => {
    sendRunner(async () => {
      const res = await verifyPhoneSendOtp(phone);
      if (res?.status === "OK") {
        setView(VIEW.VERIFY);
      }
      return res;
    });
  };

  // ===============================================
  // VERIFY OTP HANDLER
  // ===============================================
  const verifyOtpHandler = () =>
    verifyRunner(async () => {
      const res = await verifyPhoneOtp(phone, otp.join(""));
      if (res?.status === "OK") {
        setView(VIEW.SUCCESS);
      }
      return res;
    });

  return (
    <>
      {!(view === VIEW.SUCCESS) && (
        <Modal
          onClose={
            sendLoading || verifyLoading ? undefined : () => setOpen(false)
          }
          modalWidth="verifyPhoneModal"
        >
          <>
            {(sendError || verifyError) && (
              <p className="text-center text-sm text-red-500 pb-3">
                {handleErrMessage(sendError || verifyError)}
              </p>
            )}
            {view === VIEW.SEND && (
              <>
                <div className="flex items-center gap-2 justify-center md:mb-4">
                  <Smartphone />
                  <h2 className="text-lg font-semibold">Verify Phone Number</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  We will send a one-time password (OTP) to your mobile number
                  for verification.
                </p>
                <div className="border border-gray-300 rounded-md px-4 py-2 mb-4 bg-gray-50 text-sm font-medium text-left">
                  📱 {phone}
                </div>
                <ActionButtons
                  primaryText="Send OTP"
                  loading={sendLoading}
                  disabled={sendLoading}
                  onClick={sendOtpHandler}
                  onCancel={() => setOpen(false)}
                />
              </>
            )}

            {view === VIEW.VERIFY && (
              <>
                <h3 className="text-center text-xl pb-2 font-figtreeMedium">
                  OTP Verification
                </h3>
                <p className="text-center text-gray-600 max-md:text-sm">
                  Enter the OTP sent to {phone}
                </p>

                <OtpInput
                  otp={otp}
                  inputRefs={inputRefs}
                  handleOtpChange={handleOtpChange}
                  handleKeyDown={handleKeyDown}
                  handlePaste={handlePaste}
                />
                <ActionButtons
                  primaryText="Verify OTP"
                  loading={verifyLoading}
                  disabled={otp.join("").length !== 6 || verifyLoading}
                  onClick={verifyOtpHandler}
                  onCancel={() => setOpen(false)}
                />
              </>
            )}
          </>
        </Modal>
      )}
      {view === VIEW.SUCCESS && (
        <Modal onClose={() => navigate(0)} modalWidth="verifyPhoneModal">
          <img src={success} alt="success" className="mx-auto" />
          <h4 className="text-xl font-figtreeMedium pt-2">Verified</h4>
          <p className="pb-4 max-md:text-sm md:px-11">
            Your phone number has been verified successfully.
          </p>
          <button
            className="verifyPhoneButton hover:bg-db2"
            onClick={() => navigate(0)}
          >
            Ok
          </button>
        </Modal>
      )}
    </>
  );
};

export default MobileVerify;

const ActionButtons = ({
  primaryText,
  onClick,
  loading,
  disabled,
  onCancel,
}) => {
  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        className={`verifyPhoneButton ${
          disabled ? "opacity-70 cursor-not-allowed" : "hover:bg-db2"
        }`}
        disabled={disabled}
        onClick={onClick}
      >
        {loading ? (
          <span className="flex items-center">
            <LoaderCircle className="animate-spin mr-2" size={20} />
            Loading...
          </span>
        ) : (
          primaryText
        )}
      </button>

      <GrayButton btnText="Cancel" onClick={onCancel} />
    </div>
  );
};
